import Swal from 'sweetalert2';
import { getApiErrorMessage } from '../helpers';
import { getCompanyDetails } from '../services/CompanyServices';
import { getAllProducts } from '../services/ProductServices';
import { getAllPlantConfigurations } from '../services/PlantConfigurationServices';

export function normalizeDcSelectInputs(inputs) {
  if (!inputs) return inputs;
  const idFields = [
    'production_plant_id',
    'production_grade_id',
    'purchase_grade_id',
    'received_grade_id',
    'other_grade_id',
    'plant_id',
    'work_schedule_id',
    'vehicle_id',
  ];
  const normalized = { ...inputs };
  idFields.forEach((key) => {
    const value = normalized[key];
    if (value !== undefined && value !== null && value !== '') {
      const num = Number(value);
      normalized[key] = Number.isNaN(num) ? value : num;
    }
  });
  return normalized;
}

export function mapFinishedGoods(productList) {
  return (productList.product_list || [])
    .filter((product) => product.status && product.category.name === 'FINISHED GOODS')
    .map((product) => ({ value: product.id, label: product.name }));
}

export function mapFilteredPlants(company) {
  return (company.plants || []).map((plant) => ({
    value: plant.id,
    label: plant.alias,
  }));
}

export function reloadDcFormLookups(cookies, draft, setters = {}) {
  const { setFilteredPlants, setFinishedGoods, setPlantConfig } = setters;
  const ws = draft?.selectedWorkSchedule;
  const tasks = [];

  if (setFinishedGoods && cookies?.myToken) {
    tasks.push(
      getAllProducts(cookies).then((productList) => {
        setFinishedGoods(mapFinishedGoods(productList));
      }),
    );
  }

  const companyId = ws?.sales_order_detail?.sales_order?.company?.id;
  if (setFilteredPlants && companyId && cookies?.myToken) {
    tasks.push(
      getCompanyDetails(cookies, companyId).then((company) => {
        setFilteredPlants(mapFilteredPlants(company));
      }),
    );
  }

  const plantId = ws?.plant?.id || draft?.inputs?.plant_id;
  if (setPlantConfig && plantId && cookies?.myToken) {
    tasks.push(
      getAllPlantConfigurations(cookies, plantId).then((plantConfigList) => {
        setPlantConfig(plantConfigList);
      }),
    );
  }

  return Promise.all(tasks).catch(() => {});
}

export const DC_FORM_RETURN_DRAFT_KEY = 'bharatconcrete:dcFormReturnDraft';

export function isVehicleExpiryError(message) {
  const text = String(message || '').toLowerCase();
  return text.includes("vehicle's") && text.includes('expired');
}

export function saveDcFormReturnDraft(returnPath, draft) {
  if (!returnPath) return;
  sessionStorage.setItem(
    DC_FORM_RETURN_DRAFT_KEY,
    JSON.stringify({
      returnPath,
      savedAt: Date.now(),
      ...draft,
    }),
  );
}

export function consumeDcFormReturnDraft(returnPath) {
  const raw = sessionStorage.getItem(DC_FORM_RETURN_DRAFT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed.returnPath !== returnPath) {
      return null;
    }
    sessionStorage.removeItem(DC_FORM_RETURN_DRAFT_KEY);
    return parsed;
  } catch {
    sessionStorage.removeItem(DC_FORM_RETURN_DRAFT_KEY);
    return null;
  }
}

export function openEquipmentFormFromDc({ navigate, returnPath, equipmentId, draft }) {
  if (!equipmentId || !navigate) return;
  if (draft) {
    saveDcFormReturnDraft(returnPath, draft);
  }
  navigate(`/EquipmentEdit/${equipmentId}`, { state: { returnTo: returnPath } });
}

export function tryHandleVehicleExpiryError(error, { navigate, returnPath, equipmentId, draft }) {
  const message = getApiErrorMessage(error);
  if (!isVehicleExpiryError(message) || !equipmentId || !navigate) {
    return false;
  }

  Swal.fire({
    title: 'Save Failed',
    html: `<p><i>${message}</i></p><p>Update Insurance, FC, and Permit due dates in the Equipment form, then return here to save the Delivery Challan.</p>`,
    icon: 'error',
    showCancelButton: true,
    confirmButtonText: 'Open Equipment Form',
    cancelButtonText: 'Close',
    customClass: { popup: 'swal-wide' },
  }).then((result) => {
    if (result.isConfirmed) {
      openEquipmentFormFromDc({ navigate, returnPath, equipmentId, draft });
    }
  });
  return true;
}

export function restoreDcFormDraftIfReturned(location, returnPath, applyDraft) {
  if (!location.state?.fromEquipment) {
    return false;
  }
  const draft = consumeDcFormReturnDraft(returnPath);
  if (!draft) {
    return false;
  }
  applyDraft(draft);
  Swal.fire('Equipment updated. Please save the delivery challan again.', '', 'success');
  return true;
}
